import type { ReactNode } from 'react'

import { CLSize } from '~/components/component-library'
import {
  baseConfig,
  smallConfig,
} from '~/components/component-library/internal/config'
import { cn } from '~/utils/tailwind-utils'

export const OneLineFragment = ({
  prefix,
  suffix,
  onSuffixClick,
  meta,
  children,
  asChild = false,
  size = 'base',
  // Only for the CL page to visualize layout regions — do not use elsewhere
  _showBg = false,
}: {
  prefix?: ReactNode
  suffix?: ReactNode
  onSuffixClick?: (e: React.MouseEvent) => void
  meta?: ReactNode
  children: ReactNode | ReactNode[]
  asChild?: boolean
  size?: CLSize
  _showBg?: boolean
}) => {
  const config = size === 'base' ? baseConfig : smallConfig

  const suffixClassName = cn(
    'flex flex-shrink-0 items-center justify-center text-secondary',
    {
      'min-h-6 min-w-5 [&>svg]:size-4': size === 'base',
      'min-h-5 min-w-4 [&>svg]:size-3.5': size === 'small',
      'bg-blue-500': _showBg,
    },
  )

  const content = (
    <>
      {prefix && (
        <div
          className={cn('flex flex-shrink-0 items-center justify-center', {
            'min-h-6 min-w-5 [&>svg]:size-4': size === 'base',
            'min-h-5 min-w-4 [&>svg]:size-3.5': size === 'small',
            'bg-blue-500': _showBg,
          })}
        >
          {prefix}
        </div>
      )}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-row items-center',
          config.leading,
          config.horizontalGap,
          {
            'text-base': size === 'base',
            'text-sm': size === 'small',
            'bg-blue-500': _showBg,
          },
        )}
      >
        {children}
      </div>
      {meta && (
        <div
          className={cn(
            'flex flex-shrink-0 flex-row items-center justify-end whitespace-nowrap text-secondary',
            config.leading,
            config.horizontalGap,
            {
              'min-h-6 text-base': size === 'base',
              'min-h-5 text-sm': size === 'small',
              'bg-blue-500': _showBg,
            },
          )}
        >
          {meta}
        </div>
      )}
      {suffix &&
        (onSuffixClick ? (
          <button
            type="button"
            onClick={onSuffixClick}
            className={cn(suffixClassName, 'cursor-pointer')}
          >
            {suffix}
          </button>
        ) : (
          <div className={suffixClassName}>{suffix}</div>
        ))}
    </>
  )

  if (asChild) {
    return content
  } else {
    return (
      <div
        className={cn(
          'flex w-full items-start',
          config.horizontalPadding,
          config.verticalPadding,
          config.horizontalGap,
          config.leading,
        )}
      >
        {content}
      </div>
    )
  }
}

export const TwoLineFragment = ({
  topLine,
  bottomLine,
  size = 'base',
  asChild = false,
}: {
  size?: CLSize
  topLine?: ReactNode
  bottomLine?: ReactNode
  asChild?: boolean
}) => {
  const config = size === 'base' ? baseConfig : smallConfig

  const content = (
    <>
      {topLine && (
        <div
          className={cn(
            'flex min-w-0 items-center text-primary',
            config.leading,
            {
              'text-base': size === 'base',
              'text-sm': size === 'small',
            },
          )}
        >
          {topLine}
        </div>
      )}
      {bottomLine && (
        <div
          className={cn(
            'flex min-w-0 items-center text-secondary',
            config.subLeading,
            {
              'text-sm': size === 'base',
              'text-xs': size === 'small',
            },
          )}
        >
          {bottomLine}
        </div>
      )}
    </>
  )

  if (asChild) {
    return content
  } else {
    return <div className="flex min-w-0 flex-1 flex-col gap-0.5">{content}</div>
  }
}
